import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, Paper, Chip, Divider, Alert, Skeleton,
  Radio, RadioGroup, FormControlLabel, Box, useTheme, alpha,
  Avatar, List, ListItem, ListItemAvatar, ListItemText,
} from '@mui/material';
import {
  Groups as FamilyIcon, Person as PersonIcon,
  CheckCircle, Add,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import {
  admissionApplicationService, FamilyMatch,
} from '../../../services/admissions/admissionApplicationService';
import { AdmissionApplication } from '../../../types/admissions';

interface Props {
  open: boolean;
  application: AdmissionApplication;
  onCancel: () => void;
  onDone: () => Promise<void> | void;
}

/**
 * ResolveFamilyDialog — the staff step from Journey 1 Step 6. Takes the
 * raw pendingGuardians[] on an application and either:
 *   (a) attaches the applicant Person to an existing Family whose Guardian
 *       CNIC matches, or
 *   (b) creates fresh Guardian records in the throwaway Family (promoting
 *       it to `resolved`).
 *
 * The backend returns potential matches via /family-matches; the dialog
 * ranks them with the top match pre-selected. "Create new family" is always
 * available as a fallback.
 */

type Choice = { kind: 'attach'; familyId: string } | { kind: 'create_new' };

const ResolveFamilyDialog: React.FC<Props> = ({ open, application, onCancel, onDone }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [matches, setMatches] = useState<FamilyMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState<Choice | null>(null);

  const pending = application.pendingGuardians || [];

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const list = await admissionApplicationService.getFamilyMatches(application._id);
        if (!cancelled) {
          setMatches(list);
          // Default: top match if any, else create-new.
          setChoice(
            list.length > 0
              ? { kind: 'attach', familyId: list[0].family._id }
              : { kind: 'create_new' },
          );
        }
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err?.message || 'Failed to load family matches', { variant: 'error' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, application._id, enqueueSnackbar]);

  const close = () => { if (!busy) onCancel(); };

  const submit = async () => {
    if (!choice) return;
    setBusy(true);
    try {
      if (choice.kind === 'attach') {
        await admissionApplicationService.resolveFamily(application._id, {
          mode: 'attach',
          targetFamilyId: choice.familyId,
        });
      } else {
        await admissionApplicationService.resolveFamily(application._id, { mode: 'create_new' });
      }
      enqueueSnackbar('Family resolved', { variant: 'success' });
      await onDone();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to resolve family', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const topMatchCount = matches.length;
  const hasFatherInPending = pending.some(g => g.relation === 'father');

  const summary = useMemo(() => {
    if (topMatchCount === 0) {
      return 'No existing families share a Guardian CNIC with this applicant. Create a new family from the captured pending guardians.';
    }
    return `${topMatchCount} existing famil${topMatchCount === 1 ? 'y matches' : 'ies match'} the pending Guardian CNIC(s). Pick one to attach, or create a new family if none fits.`;
  }, [topMatchCount]);

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>Resolve Family</Typography>
        <Typography variant="caption" color="text.secondary">
          Required before enrollment handover.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* Pending guardians overview */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, mb: 1.5, display: 'block' }}>
              Pending guardians on this application ({pending.length})
            </Typography>
            {pending.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No pending guardian records. The application can only be resolved via "Create new family"
                (no guardians will be created — the Family is empty).
              </Typography>
            ) : (
              <Stack spacing={1}>
                {pending.map((g, i) => (
                  <Stack key={`pg-${i}`} direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.dark, width: 28, height: 28 }}>
                      <PersonIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600} noWrap>{g.name}</Typography>
                        <Chip size="small" label={g.relation} variant="outlined" sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }} />
                      </Stack>
                      <Typography variant="caption" color="text.disabled" fontFamily="monospace">
                        CNIC {g.cnic} · {g.phone}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>

          {/* Instructions */}
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
            <Typography variant="body2">{summary}</Typography>
          </Alert>

          {/* Choice list */}
          {loading ? (
            <Stack spacing={1}>
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
            </Stack>
          ) : (
            <RadioGroup
              value={choice?.kind === 'attach' ? `attach:${choice.familyId}` : 'create_new'}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'create_new') setChoice({ kind: 'create_new' });
                else setChoice({ kind: 'attach', familyId: v.slice('attach:'.length) });
              }}
            >
              <Stack spacing={1}>
                {matches.map((m) => (
                  <MatchCard
                    key={m.family._id}
                    match={m}
                    selected={choice?.kind === 'attach' && choice.familyId === m.family._id}
                  />
                ))}
                {/* Create new family option */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2, borderRadius: 2,
                    borderColor: choice?.kind === 'create_new' ? theme.palette.primary.main : theme.palette.divider,
                    bgcolor: choice?.kind === 'create_new' ? alpha(theme.palette.primary.main, 0.03) : undefined,
                    cursor: 'pointer',
                    transition: 'border-color 160ms',
                  }}
                  onClick={() => setChoice({ kind: 'create_new' })}
                >
                  <FormControlLabel
                    value="create_new"
                    control={<Radio size="small" />}
                    label={
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', width: 32, height: 32 }}>
                          <Add fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>Create new family</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pending.length > 0
                              ? `Promote the ${pending.length} pending guardian${pending.length === 1 ? '' : 's'} to real Guardian records.`
                              : 'An empty Family will be created — add Guardians later via the Family admin page.'}
                            {hasFatherInPending && pending.length > 0 && (
                              <> The father becomes the primary contact and fee payer by default.</>
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Paper>
              </Stack>
            </RadioGroup>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          onClick={submit}
          disabled={busy || !choice}
        >
          {busy
            ? 'Resolving…'
            : choice?.kind === 'attach'
              ? 'Attach to existing family'
              : 'Create new family'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MatchCard: React.FC<{ match: FamilyMatch; selected: boolean }> = ({ match, selected }) => {
  const theme = useTheme();
  const { family, people, guardians, matchedGuardianIds } = match;
  const enrolled = people.filter(p => p.kind === 'student');
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2, borderRadius: 2,
        borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.03) : undefined,
        cursor: 'pointer',
        transition: 'border-color 160ms',
      }}
    >
      <FormControlLabel
        value={`attach:${family._id}`}
        control={<Radio size="small" />}
        label={
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%' }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark', width: 32, height: 32 }}>
              <FamilyIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" fontWeight={700}>
                  {family.displayName || 'Family'}
                </Typography>
                <Chip
                  size="small"
                  label={family.status}
                  color={family.status === 'resolved' ? 'success' : 'warning'}
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10, textTransform: 'capitalize' }}
                />
                <Chip
                  size="small"
                  label={`${matchedGuardianIds.length} guardian${matchedGuardianIds.length === 1 ? '' : 's'} match`}
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10 }}
                />
              </Stack>
              {people.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {people.length} family member{people.length === 1 ? '' : 's'}
                  {enrolled.length > 0 && (
                    <> · {enrolled.length} enrolled: {enrolled.map(p => `${p.name} (GR ${p.grNumber})`).join(', ')}</>
                  )}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <List disablePadding dense>
                {guardians.slice(0, 3).map(g => (
                  <ListItem key={g._id} disableGutters sx={{ py: 0.25 }}>
                    {matchedGuardianIds.includes(g._id) && (
                      <ListItemAvatar sx={{ minWidth: 24 }}>
                        <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
                      </ListItemAvatar>
                    )}
                    <ListItemText
                      primary={
                        <Typography variant="caption" fontWeight={matchedGuardianIds.includes(g._id) ? 700 : 500}>
                          {g.name} · {g.relation} · {g.phone}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.disabled" fontFamily="monospace">
                          CNIC {g.cnic}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                {guardians.length > 3 && (
                  <Typography variant="caption" color="text.disabled" sx={{ pl: matchedGuardianIds.length > 0 ? 3 : 0 }}>
                    + {guardians.length - 3} more
                  </Typography>
                )}
              </List>
            </Box>
          </Stack>
        }
        sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
      />
    </Paper>
  );
};

export default ResolveFamilyDialog;
